import { Moment, QuickTip } from '@chipinside/frontend'
import { Translate } from '@chipinside/translate'
import { Box, Divider, Stack, Typography } from '@mui/material'
import { isEmpty, isNil, omit, omitBy, pick, uniqueId } from 'lodash'
import React from 'react'

import { getItems } from '#/components/FormatCell/Types/Infos/fields'
import { Icon } from '#/components/Icon'

type TypeInfosProps = {
  items?: Record<string, any>
  creators?: Partial<{
    created_by: string | null
    created_at: string | null
    updated_at: string | null
    updated_by: string | null
    deleted_at: string | null
    confirmed_at: string | null
    confirmed_by: string | null
    job_name: string | null
  }>
}

type InfoRowProps = {
  label: string
  children: React.ReactNode
  ellipsis?: boolean
  component?: any
}

/**
 * Extrai metadados de um objeto de valor, incluindo informações de criação, atualização e exclusão
 * @param value Objeto contendo os dados
 * @returns Objeto com metadados separados e o restante dos dados
 */
const extractMetadata = (value: TypeInfosProps['items']) => {
  // Seleciona campos específicos do objeto, incluindo caminhos aninhados
  const picked = pick(value, [
    'user.name',
    'creator',
    'creator.name',
    'creator_name',
    'created_by',
    'created_at',
    'updated_at',
    'updated_by',
    'deleted_at',
    'confirmed_at',
    'confirmed_by',
    'confirmer.name',
    'created_item_at',
    'job_name',
  ])

  const metadata = {
    confirmed_at: picked.confirmed_at ? Moment.view(picked.confirmed_at) : null,
    confirmed_by: picked.confirmed_by ?? picked['confirmer.name'],
    created_at: picked.created_at ? Moment.view(picked.created_at) : null,
    created_by:
      picked['created_item_at'] ??
      picked['creator.name'] ??
      picked['user.name'] ??
      picked['creator'] ??
      picked.creator_name ??
      picked.created_by ??
      null,
    deleted_at: picked.deleted_at ? Moment.view(picked.deleted_at) : null,
    job_name: picked.job_name,
    rest: omit(value, Object.keys(picked)) as TypeInfosProps['items'],
    updated_at: picked.updated_at ? Moment.view(picked.updated_at) : null,
    updated_by: picked.updated_by,
  }

  return omitBy(metadata, isNil)
}

/**
 * Componente para exibir uma linha de informação com rótulo e valor
 */
export const InfoRow = ({ children, component, ellipsis = false, label }: InfoRowProps) => {
  const styled = ellipsis
    ? { maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
    : undefined

  return (
    <Box sx={{ minWidth: '50%' }}>
      {label && <Typography component={component}>{label}:</Typography>}
      <Typography
        component={component}
        sx={{
          fontWeight: '500',
          ...(component && label ? { marginLeft: 0.5 } : styled),
        }}
      >
        {children}
      </Typography>
    </Box>
  )
}

/**
 * Componente que exibe as informações em um formato organizado
 */
export const PlotlineInfo: React.FC<TypeInfosProps> = ({ creators, items }) => {
  const {
    confirmed_at,
    confirmed_by,
    created_at,
    created_by,
    deleted_at,
    job_name,
    updated_at,
    updated_by,
  } = creators ?? {}
  const hasCreators = !isEmpty(creators)

  const separator = !isEmpty(items) && hasCreators ? <Divider sx={{ mt: 1.5 }} /> : null

  return (
    <Box sx={{ display: 'flex', fontSize: 'smaller' }}>
      <Box sx={{ width: '100%' }}>
        {/* Seção para dados principais */}
        {!isEmpty(items) && (
          <Stack spacing={0.5} sx={{ mt: 1.5 }}>
            {items.map(({ label, value }) => (
              <InfoRow label={label as string} component={'span'} key={label ?? uniqueId()}>
                {value}
              </InfoRow>
            ))}
          </Stack>
        )}

        {/* Separados de seção */}
        {separator}

        {/* Seção de dados de criação/atualização */}
        {hasCreators && (
          <Stack
            direction="row"
            sx={{ flexFlow: 'wrap', justifyContent: 'space-between', mt: 1.5, rowGap: 1 }}
          >
            {created_at && (
              <InfoRow label={Translate({ messageKey: 'created_item_at' })}>{created_at}</InfoRow>
            )}
            {created_by && (
              <InfoRow label={Translate({ messageKey: 'created_by' })} ellipsis>
                {created_by}
              </InfoRow>
            )}
            {confirmed_by && (
              <InfoRow label={Translate({ messageKey: 'confirmed_by' })}>{confirmed_by}</InfoRow>
            )}
            {confirmed_at && (
              <InfoRow label={Translate({ messageKey: 'confirmed_at' })}>{confirmed_at}</InfoRow>
            )}
            {updated_by && (
              <InfoRow label={Translate({ messageKey: 'updated_by' })}>{updated_by}</InfoRow>
            )}
            {updated_at && (
              <InfoRow label={Translate({ messageKey: 'updated_at' })}>{updated_at}</InfoRow>
            )}
            {deleted_at && (
              <InfoRow label={Translate({ messageKey: 'deleted_at' })}>{deleted_at}</InfoRow>
            )}
            {job_name && <InfoRow label={Translate({ messageKey: 'import' })}>{job_name}</InfoRow>}
          </Stack>
        )}
      </Box>
    </Box>
  )
}

/**
 * Componente principal que renderiza um tooltip com informações detalhadas
 * Só é exibido se o valor não estiver vazio
 */
export default function TypeInfos({ value }: { value: Record<string, any> }) {
  if (isEmpty(value)) return null

  const metadata = extractMetadata(value)
  const { rest, ...creators } = metadata
  const items = getItems(rest)?.filter(({ value }) => value)

  const hasCreators = !isEmpty(creators)

  const showTip = Boolean(hasCreators || items.length)

  return (
    showTip && (
      <div style={{ display: 'flex' }}>
        <QuickTip
          title={Translate({ messageKey: 'information_plural' })}
          icon={<Icon name="info-outlined" />}
          description={
            <PlotlineInfo items={items} creators={creators as TypeInfosProps['creators']} />
          }
          slotProps={{
            icon: {
              color: '#666',
            },
            paper: {
              backgroundColor: '#fff',
              gap: 0,
            },
            title: {
              color: theme => theme.palette.primary.main,
            },
          }}
        />
      </div>
    )
  )
}
